import { useMemo } from "react";
import { useListAlertsQuery } from "../actions/alertsInQuery";
import { useListAlertsCountsQuery } from "../actions/alertsCountsInQuery";
import { useListDevicesQuery } from "../actions/devicesInQuery";
import { useListTrapSitesQuery } from "../actions/trapsiteInQuery";
import { useParams, useLocation, matchPath } from "react-router-dom";
import { ROUTES } from "../config/routes";
import type { Dayjs } from "dayjs";
import type { AlertSorter } from "../types/api/request/alert";
import { useListDetectionsQuery } from "../actions";

export const useTrapsiteIds = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const isLocationPath = !!matchPath(ROUTES.location_detail, location.pathname);

  const { data: trapSites, isLoading: isLoadingTrapSites } =
    useListTrapSitesQuery(undefined, {
      skip: !isLocationPath,
    });

  const isUnassignedLocation = useMemo(() => {
    if (!isLocationPath || !id || !trapSites?.items) return false;
    return !trapSites.items.some((t) => t._id === id);
  }, [isLocationPath, id, trapSites]);

  return {
    selectedTrapSiteId: isLocationPath ? id : undefined,
    isUnassignedLocation,
    isLocationPath,
    trapSites,
    isLoadingTrapSites,
  };
};

export const useDeviceUuids = (options?: { pollingInterval?: number }) => {
  const {
    selectedTrapSiteId: id,
    isUnassignedLocation,
    isLocationPath,
    trapSites,
    isLoadingTrapSites,
  } = useTrapsiteIds();

  const { data: devices, isLoading: isLoadingDevices } = useListDevicesQuery(
    undefined,
    { pollingInterval: options?.pollingInterval },
  );

  const filteredDevices = useMemo(() => {
    if (!devices?.items) return [];

    if (!isLocationPath || !id) {
      // Not on a location page — return all devices
      return devices.items;
    }

    // Still loading trap sites — don't return incorrect results
    if (!trapSites?.items) return [];

    if (isUnassignedLocation) {
      // Unassigned location — return devices with no trap_site_id
      return devices.items.filter((device) => !device.trap_site_id);
    }

    // Specific location — return devices assigned to this trap site
    return devices.items.filter((device) => device.trap_site_id === id);
  }, [devices, isLocationPath, id, isUnassignedLocation, trapSites]);

  return {
    deviceUuids:
      filteredDevices.flatMap((device) => (device.uuid ? [device.uuid] : [])) ||
      [],
    hasDevices: filteredDevices.length > 0,
    isLoadingDevices:
      isLoadingDevices || (isLocationPath && isLoadingTrapSites),
    devices: filteredDevices,
  };
};

export const useAlerts = (options?: {
  page?: number;
  pageSize?: number;
  msg_types?: string[];
  types?: ("detection" | "maintenance" | "notification")[];
  skip?: boolean;
  pollingInterval?: number;
  idSearch?: string;
  descSearch?: string;
  dateRange?: [Dayjs | null, Dayjs | null] | null;
  deviceIds?: string[];
  statuses?: string[];
  sorter?: AlertSorter;
}) => {
  const {
    page,
    pageSize,
    types,
    msg_types,
    skip,
    pollingInterval,
    idSearch,
    descSearch,
    deviceIds = [],
    statuses,
    sorter,
    dateRange,
  } = options ?? {};
  const { selectedTrapSiteId } = useTrapsiteIds();
  const { deviceUuids, hasDevices, isLoadingDevices } = useDeviceUuids();

  const devices = deviceIds.length > 0 ? deviceIds : deviceUuids;

  const queryResult = useListAlertsQuery(
    {
      idSearch,
      descSearch,
      page,
      pageSize,
      devices,
      statuses,
      trap_site_id: selectedTrapSiteId,
      types,
      msg_types,
      sorter,
      dateAfter: dateRange?.[0]?.toISOString(),
      dateBefore: dateRange?.[1]?.endOf("day")?.toISOString(),
    },
    {
      skip:
        skip ||
        (deviceIds.length === 0 && !hasDevices) ||
        (!hasDevices && (!selectedTrapSiteId || !types?.includes("detection"))),
      pollingInterval,
    },
  );

  return {
    ...queryResult,
    hasDevices,
    isLoading: queryResult.isLoading || isLoadingDevices,
  };
};

export const useDetections = (options?: {
  page?: number;
  pageSize?: number;
  invasive?: boolean;
  skip?: boolean;
  pollingInterval?: number;
}) => {
  const { page, pageSize, invasive, skip, pollingInterval } = options ?? {};

  const queryResult = useListDetectionsQuery(
    {
      page,
      pageSize,
      invasive,
    },
    { skip: skip, pollingInterval },
  );

  return {
    ...queryResult,
    isLoading: queryResult.isLoading,
  };
};

export const useMaintenanceAlertsCounts = () => {
  const { deviceUuids, hasDevices, isLoadingDevices } = useDeviceUuids();

  const queryResult = useListAlertsCountsQuery(
    {
      devices: deviceUuids,
      types: ["maintenance"],
      msg_types: [],
    },
    { skip: !hasDevices },
  );

  return {
    ...queryResult,
    counts: queryResult.data?.items ?? {},
    isLoading: queryResult.isLoading || isLoadingDevices,
  };
};

export const useDetectionAlertsCounts = () => {
  const { selectedTrapSiteId } = useTrapsiteIds();
  const { deviceUuids, hasDevices, isLoadingDevices } = useDeviceUuids();

  const queryResult = useListAlertsCountsQuery(
    {
      devices: deviceUuids,
      trap_site_id: selectedTrapSiteId,
      types: ["detection"],
      msg_types: [],
    },
    { skip: !hasDevices },
  );

  return {
    ...queryResult,
    counts: queryResult.data?.items ?? {},
    isLoading: queryResult.isLoading || isLoadingDevices,
  };
};
